"use client";

import { useEffect, useCallback } from "react";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";
import { Button } from "./Button";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: React.ReactNode;
  size?: "sm" | "md" | "lg" | "xl" | "full";
  showCloseButton?: boolean;
  closeOnOverlayClick?: boolean;
  closeOnEsc?: boolean;
  className?: string;
}

const sizeClasses = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  full: "max-w-4xl",
};

export function Modal({
  isOpen,
  onClose,
  title,
  description,
  children,
  size = "md",
  showCloseButton = true,
  closeOnOverlayClick = true,
  closeOnEsc = true,
  className,
}: ModalProps) {
  const handleEsc = useCallback(
    (e: KeyboardEvent) => {
      if (closeOnEsc && e.key === "Escape") {
        onClose();
      }
    },
    [closeOnEsc, onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleEsc);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "";
    };
  }, [isOpen, handleEsc]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={closeOnOverlayClick ? onClose : undefined}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        className={cn(
          "relative bg-white rounded-lg shadow-xl w-full mx-4",
          sizeClasses[size],
          className
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? "modal-title" : undefined}
        aria-describedby={description ? "modal-description" : undefined}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <div className="flex items-start justify-between p-4 border-b border-gray-200">
            <div>
              {title && (
                <h2
                  id="modal-title"
                  className="text-lg font-semibold text-gray-900"
                >
                  {title}
                </h2>
              )}
              {description && (
                <p
                  id="modal-description"
                  className="mt-1 text-sm text-gray-500"
                >
                  {description}
                </p>
              )}
            </div>
            {showCloseButton && (
              <button
                onClick={onClose}
                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "danger" | "primary";
  loading?: boolean;
}

export function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  variant = "primary",
  loading = false,
}: ConfirmModalProps) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="sm">
      <p className="text-sm text-gray-600 mb-6">{message}</p>
      <div className="flex justify-end gap-3">
        <Button variant="secondary" onClick={onClose} disabled={loading}>
          {cancelLabel}
        </Button>
        <Button
          variant={variant === "danger" ? "danger" : "primary"}
          onClick={onConfirm}
          loading={loading}
        >
          {confirmLabel}
        </Button>
      </div>
    </Modal>
  );
}

interface ModalFooterProps {
  children: React.ReactNode;
  className?: string;
}

export function ModalFooter({ children, className }: ModalFooterProps) {
  return (
    <div
      className={cn(
        "flex justify-end gap-3 pt-4 border-t border-gray-200 -mx-4 -mb-4 px-4 pb-4",
        className
      )}
    >
      {children}
    </div>
  );
}

// Lightbox for viewing images
import { ChevronLeft, ChevronRight, Edit, Download, Loader2 } from "lucide-react";
import Image from "next/image";

interface LightboxPhoto {
  url: string;
  alt?: string;
  id?: number; // Photo DB id for editing
  filename?: string; // Download filename (e.g., UHBG001_0.jpg)
  caption?: string; // Optional caption text (e.g., painting title)
  subcaption?: string; // Optional secondary text (e.g., artist name)
}

interface LightboxProps {
  photos: LightboxPhoto[];
  currentIndex: number;
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (index: number) => void;
  onEdit?: (photoId: number, photoUrl: string) => void; // Optional edit callback
  totalCount?: number; // Total items in collection (for infinite scroll galleries)
  globalIndex?: number; // Current position in total collection
  isLoading?: boolean; // Show loading indicator when fetching more
  showDownload?: boolean; // Show download button (default: true)
}

export function Lightbox({
  photos,
  currentIndex,
  isOpen,
  onClose,
  onNavigate,
  onEdit,
  totalCount,
  globalIndex,
  isLoading,
  showDownload = true,
}: LightboxProps) {
  // For infinite scroll galleries, check if more content might be available
  const actualTotal = totalCount ?? photos.length;
  const actualGlobalIndex = globalIndex ?? currentIndex;
  const mightHaveMore = totalCount !== undefined && actualGlobalIndex < totalCount - 1;

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "ArrowLeft") {
        // Allow navigating backward if we have previous or might load more
        if (currentIndex > 0 || (globalIndex !== undefined && globalIndex > 0)) {
          onNavigate(currentIndex - 1);
        }
      } else if (e.key === "ArrowRight") {
        // Allow navigating forward if we have next or might load more
        if (currentIndex < photos.length - 1 || mightHaveMore) {
          onNavigate(currentIndex + 1);
        }
      }
    },
    [onClose, onNavigate, currentIndex, photos.length, globalIndex, mightHaveMore]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen || photos.length === 0) return null;

  const currentPhoto = photos[currentIndex];
  const hasPrev = currentIndex > 0 || (globalIndex !== undefined && globalIndex > 0);
  const hasNext = currentIndex < photos.length - 1 || mightHaveMore;
  const canEdit = onEdit && currentPhoto.id;

  const handleDownload = () => {
    // Use server-side proxy to avoid CORS issues with external images
    const filename = currentPhoto.filename || currentPhoto.alt || "image.jpg";
    const downloadUrl = `/api/download?url=${encodeURIComponent(currentPhoto.url)}&filename=${encodeURIComponent(filename)}`;
    window.open(downloadUrl, "_blank");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Dark overlay */}
      <div
        className="fixed inset-0 bg-black/90"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Top toolbar */}
      <div className="absolute top-4 left-0 right-0 z-10 flex items-center justify-between px-4">
        {/* Counter */}
        <div className="text-white/70 text-sm flex items-center gap-2">
          {actualTotal > 1 && (
            <>
              {actualGlobalIndex + 1} / {actualTotal}
              {isLoading && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
            </>
          )}
        </div>

        {/* Action buttons */}
        {/* Action buttons */}
        {(showDownload || canEdit) && (
          <div className="flex items-center gap-2">
            {showDownload && (
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded transition-colors"
                aria-label="Download image"
              >
                <Download className="h-4 w-4" />
                <span className="text-sm">Download</span>
              </button>
            )}
            {canEdit && (
              <button
                onClick={() => onEdit(currentPhoto.id!, currentPhoto.url)}
                className="flex items-center gap-2 px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded transition-colors"
                aria-label="Edit image"
              >
                <Edit className="h-4 w-4" />
                <span className="text-sm">Edit</span>
              </button>
            )}
          </div>
        )}

        {/* Close button */}
        <button
          onClick={onClose}
          className="p-2 text-white/70 hover:text-white transition-colors"
          aria-label="Close"
        >
          <X className="h-8 w-8" />
        </button>
      </div>

      {/* Previous button */}
      {hasPrev && (
        <button
          onClick={() => onNavigate(currentIndex - 1)}
          className="absolute left-4 z-10 p-2 text-white/70 hover:text-white transition-colors"
          aria-label="Previous"
        >
          <ChevronLeft className="h-10 w-10" />
        </button>
      )}

      {/* Next button */}
      {hasNext && (
        <button
          onClick={() => onNavigate(currentIndex + 1)}
          className="absolute right-4 z-10 p-2 text-white/70 hover:text-white transition-colors"
          aria-label="Next"
        >
          <ChevronRight className="h-10 w-10" />
        </button>
      )}

      {/* Image */}
      <div className="absolute inset-16 flex items-center justify-center">
        <div className="relative w-full h-full">
          <Image
            src={currentPhoto.url}
            alt={currentPhoto.alt || "Photo"}
            fill
            className="object-contain"
            sizes="(max-width: 768px) 100vw, 90vw"
            priority
          />
        </div>
      </div>

      {/* Caption (for painting galleries) */}
      {(currentPhoto.caption || currentPhoto.subcaption) && (
        <div className="absolute bottom-4 left-0 right-0 z-10 flex justify-center px-4">
          <div className="bg-black/60 backdrop-blur-sm rounded-lg px-4 py-2 text-center max-w-lg">
            {currentPhoto.caption && (
              <p className="text-white font-medium">{currentPhoto.caption}</p>
            )}
            {currentPhoto.subcaption && (
              <p className="text-white/70 text-sm">{currentPhoto.subcaption}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
